import React from 'react';
import {
    Box,
    Container,
    Grid,
    IconButton,
    Link,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { FaTiktok } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import {
    EXPLORE_ITEMS,
    EXPLORE_MENU_LABEL,
    FOOTER_BREEDING_ITEMS,
    FOOTER_BREEDING_LABEL,
    FOOTER_MOBILE_ITEMS,
} from '../navigation';
import {
    CONTACT_EMAIL,
    LOCATION_LABEL,
    SOCIAL_LINKS,
    SOCIAL_MAILTO,
} from '../site/contact';

const footerLinkSx = {
    color: 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    '&:hover': {
        color: '#fff',
        textDecoration: 'underline',
    },
};

const sectionLabelSx = {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    mb: 1.25,
};

const socialButtonSx = {
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.22)',
        borderColor: 'rgba(255, 255, 255, 0.7)',
    },
};

type FooterLinkGroupProps = {
    label: string;
    items: ReadonlyArray<{ label: string; path: string }>;
};

const FooterLinkGroup: React.FC<FooterLinkGroupProps> = ({ label, items }) => (
    <Box>
        <Typography component="p" sx={sectionLabelSx}>
            {label}
        </Typography>
        <Stack spacing={0.75} alignItems="flex-start">
            {items.map((item) => (
                <Link
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    sx={footerLinkSx}
                >
                    {item.label}
                </Link>
            ))}
        </Stack>
    </Box>
);

const Footer: React.FC = () => {
    const year = new Date().getFullYear();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const TikTokIcon = FaTiktok as React.ComponentType<{ size?: number }>;

    const align = isMobile ? 'center' : 'left';

    return (
        <Box
            component="footer"
            sx={{
                mt: 'auto',
                backgroundColor: 'primary.main',
                color: '#fff',
            }}
        >
            <Container
                maxWidth="md"
                sx={{
                    py: { xs: 3.5, md: 4 },
                    px: { xs: 2.5, md: 3 },
                }}
            >
                <Grid
                    container
                    spacing={{ xs: 3, md: 2 }}
                    justifyContent="space-between"
                >
                    <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{ textAlign: align }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
                            Galia Standard Poodles
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            justifyContent={isMobile ? 'center' : 'flex-start'}
                            sx={{ color: 'rgba(255, 255, 255, 0.92)' }}
                        >
                            <PlaceOutlinedIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2">{LOCATION_LABEL}</Typography>
                        </Stack>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{ textAlign: align }}
                    >
                        {isMobile ? (
                            <Stack
                                component="nav"
                                aria-label="Footer navigation"
                                spacing={0.25}
                                alignItems="center"
                                sx={{ width: '100%' }}
                            >
                                {FOOTER_MOBILE_ITEMS.map((item) => (
                                    <Link
                                        key={item.path}
                                        component={RouterLink}
                                        to={item.path}
                                        sx={{
                                            ...footerLinkSx,
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: 280,
                                            py: 1,
                                            textAlign: 'center',
                                            borderRadius: 1,
                                            '&:active': {
                                                bgcolor: 'rgba(255, 255, 255, 0.12)',
                                            },
                                        }}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </Stack>
                        ) : (
                            <Box
                                component="nav"
                                aria-label="Footer navigation"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    gap: 5,
                                }}
                            >
                                <FooterLinkGroup
                                    label={FOOTER_BREEDING_LABEL}
                                    items={FOOTER_BREEDING_ITEMS}
                                />
                                <FooterLinkGroup
                                    label={EXPLORE_MENU_LABEL}
                                    items={EXPLORE_ITEMS}
                                />
                            </Box>
                        )}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{ textAlign: align }}
                    >
                        <Typography component="p" sx={sectionLabelSx}>
                            Connect
                        </Typography>
                        <Link
                            href={SOCIAL_MAILTO}
                            sx={{
                                ...footerLinkSx,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                mb: 1.5,
                                justifyContent: isMobile ? 'center' : 'flex-start',
                            }}
                        >
                            <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                            {isMobile ? 'Email us' : CONTACT_EMAIL}
                        </Link>
                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent={isMobile ? 'center' : 'flex-start'}
                            aria-label="Social media links"
                        >
                            <IconButton
                                component="a"
                                href={SOCIAL_LINKS.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                size="small"
                                sx={socialButtonSx}
                            >
                                <FacebookIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                component="a"
                                href={SOCIAL_LINKS.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                size="small"
                                sx={socialButtonSx}
                            >
                                <InstagramIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                component="a"
                                href={SOCIAL_LINKS.tiktok}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="TikTok"
                                size="small"
                                sx={socialButtonSx}
                            >
                                <TikTokIcon size={17} />
                            </IconButton>
                        </Stack>
                    </Grid>
                </Grid>

                <Typography
                    variant="caption"
                    component="p"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.72)',
                        textAlign: 'center',
                        mt: { xs: 3, md: 3.5 },
                        fontSize: '0.8rem',
                    }}
                >
                    © {year} Galia Standard Poodles. All rights reserved.
                </Typography>
            </Container>
        </Box>
    );
};

export default Footer;
